import { Component } from '@theme/component';
import { ThemeEvents, ZoomMediaSelectedEvent } from '@theme/events';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
    this.#imageSelection++;
    this.removeAttribute('aria-busy');
  }

  /**
   * Handles a product select event by replacing the current media gallery with a new one.
   *
   * @param {ProductSelectEvent} event - The product select event.
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html } = detail;
        const newMediaGallery = html.querySelector('media-gallery');
        if (!newMediaGallery) return;

        this.replaceWith(newMediaGallery);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[media-gallery] Event promise rejected:', error);
      });
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  #imageSelection = 0;

  /**
   * Keep the current photo visible until the requested gallery image is decoded.
   * Only an explicit thumbnail/dot selection starts a load; other photos stay lazy.
   * @param {number|string} input - Requested media index.
   * @param {Event} [event] - Control event.
   */
  async selectMedia(input, event) {
    event?.preventDefault();
    const selection = ++this.#imageSelection;
    this.removeAttribute('aria-busy');
    const slideshow = this.slideshow;
    const index = Number(input);
    const slide = slideshow?.slides?.[index];
    if (!slide || !Number.isInteger(index)) return;
    const previous = slideshow.current;
    if (index === previous) return;
    const image = slide.querySelector('.product-media__image');

    if (image instanceof HTMLImageElement) {
      this.setAttribute('aria-busy', 'true');
      image.loading = 'eager';
      try {
        await image.decode();
      } catch {
        // Keep a working photo visible if the requested image fails to load.
        if (!image.complete || !image.naturalWidth) return;
      } finally {
        if (selection === this.#imageSelection) this.removeAttribute('aria-busy');
      }
    }

    // Ignore stale clicks, detached galleries, or a swipe made while loading.
    if (selection !== this.#imageSelection || !this.isConnected ||
        slideshow !== this.slideshow || slideshow.current !== previous) return;
    slideshow.select(index, event, { animate: false });
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
